import React, { FC, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import tickingreencircleSvg from "@/assets/images/clinical/tickingreencircle.svg"
import { UButton, UText } from "../../../components"
import { Box, Button, Grid } from "../../../components/mui.components"
import { useAppDispatch, useAppSelector } from "../../../core/app/hooks"
import {
  disconnectScanner,
  getScannerConnectionStatus,
  getScannerRedirectUrl,
} from "../../../core/app/slices/integrations/integrationsThunkApi"
import DialogBox from "@/modules/Clinical/components/DialogBox/dialogBox"
import { UModalPopup } from "../../../ui-component"
import {
  getScannerConnected,
  isSameScannerId,
  SCANNER_INTEGRATION_IDS,
} from "@/core/app/slices/integrations/scannerConstants"
import {
  clearEasyRxOAuthPopupMarker,
  EASYRX_SCANNER,
  markEasyRxOAuthPopupOpened,
} from "./easyRxIntegrationConstants"
import {
  SCANNER_OAUTH_BROADCAST_CHANNEL,
  SCANNER_OAUTH_STORAGE_RESULT_KEY,
  SCANNER_OAUTH_TAB_ID,
  isScannerOAuthMessage,
  scannerOAuthWindowName,
} from "./scannerOAuth"
import { useScannerOAuthQueryHandler } from "./useScannerOAuthQueryHandler"

const integrationModalSx = {
  ".MuiDialog-paper": {
    maxWidth: 444,
  },
  "#titleCntr": {
    padding: "18px 24px",
  },
  "#contentCntr": {
    padding: "20px 24px",
  },
  "#btnListCntr": {
    padding: 1,
    gap: 1,
  },
}

const EasyRxConnect: FC = () => {
  const { t } = useTranslation("common")
  const dispatch = useAppDispatch()
  const {
    integrationsLoading,
    disconnectLoading,
    connectionStatus,
  } = useAppSelector((s) => s.integrationsService)
  const easyRxConnected =
    connectionStatus[SCANNER_INTEGRATION_IDS.EASYRX] === true

  const [openDisconnectPopup, setOpenDisconnectPopup] = useState(false)
  const [openSuccessPopup, setOpenSuccessPopup] = useState(false)
  const [callbackErrorOpen, setCallbackErrorOpen] = useState(false)
  const [connectErrorOpen, setConnectErrorOpen] = useState(false)
  /** OAuth window opened via CONNECT (parent only). */
  const oauthPopupRef = useRef<Window | null>(null)
  const [oauthPopupActive, setOauthPopupActive] = useState(false)
  /** Dedupe duplicate `message` + BroadcastChannel delivery in the same tick. */
  const oauthResultDedupRef = useRef<{ t: number; ok: boolean } | null>(null)

  useScannerOAuthQueryHandler({
    scannerId: EASYRX_SCANNER,
    refetchWhenNoOAuthParam: true,
  })

  /** Opener: result from OAuth popup (`postMessage` or BroadcastChannel if opener was cleared). */
  useEffect(() => {
    /**
     * Do not require `oauthPopupRef` — the poll effect may clear it when the popup closes *before*
     * the message is delivered, which used to skip this handler and leave Redux stale.
     */
    const refetchConnectionAfterOAuth = (isRetry: boolean) => {
      dispatch(getScannerConnectionStatus())
        .unwrap()
        .then((res) => {
          if (getScannerConnected(res.connections, EASYRX_SCANNER)) {
            setOpenSuccessPopup(true)
            return
          }
          if (!isRetry) {
            window.setTimeout(() => refetchConnectionAfterOAuth(true), 1500)
          } else {
            setCallbackErrorOpen(true)
          }
        })
        .catch(() => {
          if (!isRetry) {
            window.setTimeout(() => refetchConnectionAfterOAuth(true), 1500)
          } else {
            setCallbackErrorOpen(true)
          }
        })
    }

    const applyOAuthResult = (ok: boolean) => {
      const now = Date.now()
      const last = oauthResultDedupRef.current
      if (
        last &&
        now - last.t < 120 &&
        last.ok === ok
      ) {
        return
      }
      oauthResultDedupRef.current = { t: now, ok }
      oauthPopupRef.current = null
      setOauthPopupActive(false)
      clearEasyRxOAuthPopupMarker()
      if (!ok) {
        setCallbackErrorOpen(true)
        dispatch(getScannerConnectionStatus())
        return
      }
      refetchConnectionAfterOAuth(false)
    }
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      const d = event.data
      if (!isScannerOAuthMessage(d)) return
      if (!isSameScannerId(d.scanner, EASYRX_SCANNER)) return
      if (d.tabId !== SCANNER_OAUTH_TAB_ID) return
      applyOAuthResult(!!d.ok)
    }
    const bc = new BroadcastChannel(SCANNER_OAUTH_BROADCAST_CHANNEL)
    bc.onmessage = (ev: MessageEvent) => {
      const d = ev.data
      if (!isScannerOAuthMessage(d)) return
      if (!isSameScannerId(d.scanner, EASYRX_SCANNER)) return
      if (d.tabId !== SCANNER_OAUTH_TAB_ID) return
      applyOAuthResult(!!d.ok)
    }
    const onStorage = (event: StorageEvent) => {
      if (event.key !== SCANNER_OAUTH_STORAGE_RESULT_KEY || !event.newValue) return
      try {
        const d = JSON.parse(event.newValue) as unknown
        if (!isScannerOAuthMessage(d)) return
        if (!isSameScannerId(d.scanner, EASYRX_SCANNER)) return
        if (d.tabId !== SCANNER_OAUTH_TAB_ID) return
        applyOAuthResult(!!d.ok)
      } catch { /* malformed */ }
    }
    window.addEventListener("message", onMessage)
    window.addEventListener("storage", onStorage)
    return () => {
      window.removeEventListener("message", onMessage)
      window.removeEventListener("storage", onStorage)
      bc.close()
    }
  }, [dispatch])

  /** Opener: popup closed without postMessage — refetch (e.g. user closed window early). */
  useEffect(() => {
    if (!oauthPopupActive) return
    const id = window.setInterval(() => {
      const p = oauthPopupRef.current
      if (p && p.closed) {
        oauthPopupRef.current = null
        setOauthPopupActive(false)
        dispatch(getScannerConnectionStatus())
          .unwrap()
          .then((res) => {
            if (getScannerConnected(res.connections, EASYRX_SCANNER)) {
              setOpenSuccessPopup(true)
            }
          })
          .catch(() => {})
      }
    }, 500)
    return () => window.clearInterval(id)
  }, [oauthPopupActive, dispatch])

  return (
    <>
      <Box sx={{ width: "100%", pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <UText
            variant={"h5"}
            component={"div"}
            sxProp={{ textAlign: "left", pb: 0 }}
          >
            {t("settings.Integrations.easyRxTitle")}
          </UText>
          {easyRxConnected && (
            <Button
              variant={"text"}
              sx={{
                minWidth: "auto",
                height: "auto",
                width: "auto",
                lineHeight: "22px",
                py: "4px",
                px: "5px",
              }}
              disabled={disconnectLoading === "pending"}
              onClick={() => {
                setOpenDisconnectPopup(true)
              }}
            >
              {t("settings.Integrations.easyRxDisconnectBtn")}
            </Button>
          )}
        </Box>
        {!easyRxConnected && (
          <UText
            variant={"body1"}
            component={"div"}
            sxProp={{ pb: 2, color: "rgba(0,0,0,0.6)" }}
          >
            {t("settings.Integrations.easyRxDescription")}
          </UText>
        )}
        {easyRxConnected ? (
          <Grid>
            <UText
              variant={"body1"}
              component={"div"}
              sxProp={{ pb: 1, color: "rgba(0,0,0,0.6)" }}
            >
              {t("settings.Integrations.easyRxStatusLabel")}
            </UText>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <UText
                variant={"body1"}
                component={"div"}
                sxProp={{ pb: 0, pr: 0.5 }}
              >
                {t("settings.Integrations.easyRxConnectedLabel")}
              </UText>
              <CheckCircleIcon color="success" />
            </Box>
          </Grid>
        ) : (
          <UButton
            onClickHandler={async () => {
              try {
                const { redirect_url } = await dispatch(
                  getScannerRedirectUrl({ scannerName: EASYRX_SCANNER }),
                ).unwrap()
                markEasyRxOAuthPopupOpened()
                const authWindow = window.open(
                  redirect_url,
                  scannerOAuthWindowName(EASYRX_SCANNER),
                  "popup=yes,width=600,height=720,left=120,top=80,scrollbars=yes,resizable=yes",
                )
                if (!authWindow) {
                  clearEasyRxOAuthPopupMarker()
                  setConnectErrorOpen(true)
                  return
                }
                oauthPopupRef.current = authWindow
                setOauthPopupActive(true)
              } catch {
                clearEasyRxOAuthPopupMarker()
                setConnectErrorOpen(true)
              }
            }}
            variant={"contained"}
            btnText={t("settings.Integrations.easyRxConnect")}
            disabled={
              integrationsLoading === "pending"
            }
            sxProp={{ width: "100%" }}
          />
        )}
      </Box>

      <DialogBox
        open={openDisconnectPopup}
        title={t("settings.Integrations.easyRxDisconnectTitle")}
        content={
          <>
            <br />
            {t("settings.Integrations.easyRxDisconnectBody")}
          </>
        }
        actions={
          <>
            <UButton
              onClickHandler={() => {
                setOpenDisconnectPopup(false)
              }}
              variant={"text"}
              btnText={t("settings.Integrations.easyRxBtnCancel")}
            />
            <UButton
              onClickHandler={() => {
                dispatch(disconnectScanner({ scannerName: EASYRX_SCANNER }))
                  .unwrap()
                  .then(() => {
                    setOpenDisconnectPopup(false)
                  })
                  .catch(() => {
                    setOpenDisconnectPopup(false)
                  })
              }}
              variant={"contained"}
              btnText={t("settings.Integrations.easyRxBtnYes")}
            />
          </>
        }
      ></DialogBox>
      <DialogBox
        open={callbackErrorOpen}
        title={t("settings.Integrations.easyRxErrorTitle")}
        content={
          <>
            <br />
            {t("settings.Integrations.easyRxCallbackError")}
          </>
        }
        actions={
          <>
            <UButton
              onClickHandler={() => {
                setCallbackErrorOpen(false)
              }}
              variant={"contained"}
              btnText={t("settings.Integrations.easyRxBtnOk")}
            />
          </>
        }
      ></DialogBox>
      <DialogBox
        open={connectErrorOpen}
        title={t("settings.Integrations.easyRxErrorTitle")}
        content={
          <>
            <br />
            {t("settings.Integrations.easyRxConnectError")}
          </>
        }
        actions={
          <>
            <UButton
              onClickHandler={() => {
                setConnectErrorOpen(false)
              }}
              variant={"contained"}
              btnText={t("settings.Integrations.easyRxBtnOk")}
            />
          </>
        }
      ></DialogBox>
      <UModalPopup
        isAlert={openSuccessPopup}
        title={
          <>
            <img
              src={tickingreencircleSvg}
              alt=""
              aria-hidden
              style={{ margin: "4px 10px 0 0" }}
            />
            {t("settings.Integrations.easyRxModalTitle")}
          </>
        }
        content={<>{t("settings.Integrations.easyRxModalBody")}</>}
        sxModalProps={integrationModalSx}
        btnList={[
          <UButton
            key="ok"
            onClickHandler={() => {
              setOpenSuccessPopup(false)
            }}
            variant={"contained"}
            btnText={t("settings.Integrations.easyRxBtnOk")}
          />,
        ]}
      ></UModalPopup>
    </>
  )
}

export default EasyRxConnect
